import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { InsightsService } from '../insights.service';
import { Subject, debounceTime, finalize, takeUntil } from 'rxjs';
import { CommonService } from '../../../../shared/services/common.service';
import { BIPApiResponsePaginated } from '../insights.interface';
import {
  ManageOrganizationDrawerParams,
  searchObj,
} from '../../manage-organization.interface';
import { OrgManagementMode } from '../../manage-organization.enums';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../core/services/drawer.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { CommonModule } from '@angular/common';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ManageOrganizationService } from '../../manage-organization.service';
import { CaseMember } from '../../../insights/insights.interface';
import { ErrorResponse } from '../../../../core/models/error.models';
@Component({
  selector: 'app-cases',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    CommonModule,
    NzTableModule,
    NzAvatarModule,
    NzDropDownModule,
    NzFormModule,
    NzModalModule,
    NzSelectModule,
  ],
  templateUrl: './cases.component.html',
  styleUrl: './cases.component.scss',
})
export class CasesComponent implements OnDestroy, OnInit {
  private readonly insightService = inject(InsightsService);
  @Input() tabIndex = 0;
  showAllMembers = false;
  loading = false;
  pageSize = 10;
  pageIndex = 1;
  total = 0;
  sort = '';
  sortOrder: number | undefined = undefined;
  searchTerm = '';
  errorMessage = '';
  statusId = null;
  isVisible = false;
  private readonly destroy$ = new Subject<void>();
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  listOfColumn = [
    {
      title: '',
      compare: null,
    },
    {
      title: 'Name',
      key: 'studentName',
    },
    {
      title: 'Student School',
      key: 'organizationName',
    },
    {
      title: 'Student ID',
      key: 'studentId',
    },

    {
      title: 'Owner',
      key: 'caseOwnerName',
    },
    {
      title: 'Status',
      key: 'status',
    },
    {
      title: 'Licensed',
      key: 'licensed',
    },
    {
      title: 'Team Member',
      key: 'teamMembers',
    },
    { title: '', compare: null },
  ];
  BIPs$ = this.insightService.BIP$;

  constructor(
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService,
    private readonly manageOrgService: ManageOrganizationService
  ) {
    this.setupDrawerParams();
    this.insightService.BIP$.subscribe(
      (res: BIPApiResponsePaginated | null) => {
        if (res) {
          this.total = res?.result.totalRows;
        }
      }
    );
  }

  onQueryParamsChange(params: NzTableQueryParams) {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    this.sortOrder =
      !!currentSort && currentSort.value
        ? currentSort.value === 'ascend'
          ? 0
          : 1
        : undefined;
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.insightService
      .getBIPList({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        sort: this.sort,
        searchTerm: this.searchTerm,
        organizationId: this.orgParams.organizationId,
        IsInsightAdminTab: false,
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  showPrintModal(): void {
    this.isVisible = true;
  }
  handleOkMiddle(): void {
    this.isVisible = false;
  }
  handleCancelMiddle(): void {
    this.isVisible = false;
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  ngOnInit() {
    this.manageOrgService.search$
      .pipe(debounceTime(1000))
      .subscribe((searchObj: searchObj) => {
        if (
          this.searchTerm !== searchObj.searchTerm &&
          searchObj.tabIndex === 2 &&
          this.tabIndex === 0
        ) {
          this.searchTerm = searchObj.searchTerm;
          if (this.pageIndex === 1) {
            this.loadData();
          } else {
            this.pageIndex = 1;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInitials(name: string): string {
    const nameParts = name.split(' '); // Split name by spaces
    const initials = nameParts
      .map(part => part[0]) // Get the first letter of each part
      .join('') // Combine the letters
      .toUpperCase(); // Convert to uppercase
    return initials;
  }

  getMemberList(caseMembers: CaseMember[]) {
    const memberInitials: string[] = [];
    caseMembers.forEach(ele => {
      memberInitials.push(this.getInitials(ele.caseMemberName));
    });
    return memberInitials;
  }

  updateCaseStatus(id: number, status: number) {
    const statusId = status === 1 ? 2 : 1;
    this.loading = true;
    this.insightService
      .updateCaseStatus(id, statusId)
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.loadData();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }
}
